import { type UserCredential } from '@linktivity/link-utils';
import { WebMixerService } from '@linktivity/triplabo-taxfree';
import i18n, { DEFAULT_LOCALE, resetLocale } from '@triplabo/web/i18n';
import { config as env } from '@triplabo/web/utils/env';
import webAuth from '@triplabo/web/vendors/firebase';

const EMAIL_FOR_SIGN_IN_KEY = 'email_for_sign_in';

export const AUTH_HEADER = 'grpc-metadata-authorization';
export const LANG_HEADER = 'Language';

export const langMiddleware: WebMixerService.Middleware = {
  pre: async ({ url, init }) => {
    const headers = new Headers(init.headers);
    headers.set(LANG_HEADER, (i18n.language || DEFAULT_LOCALE).toUpperCase());

    return { url, init: { ...init, headers } };
  }
};

export const accountApi = new WebMixerService.AccountApi(
  new WebMixerService.Configuration({
    basePath: env.apiBase,
    middleware: [langMiddleware]
  })
);

const createTokenStore = (
  accessKey: string,
  refreshKey: string,
  onExpired?: () => void
) => {
  const getAccessToken = () => localStorage.getItem(accessKey) ?? '';

  const getRefreshToken = () => localStorage.getItem(refreshKey) ?? '';

  const setTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem(accessKey, accessToken);
    localStorage.setItem(refreshKey, refreshToken);
  };

  const clearTokens = () => {
    localStorage.removeItem(accessKey);
    localStorage.removeItem(refreshKey);
  };

  let refreshing: Promise<boolean> | null = null;

  const requestNewTokens = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      onExpired?.();
      return false;
    }

    try {
      const tokens = await accountApi.webMixerServiceRefresh({
        body: { refreshToken }
      });

      setTokens(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch (error) {
      console.error(error);
      onExpired?.();
      return false;
    }
  };

  const refreshAccessToken = async () => {
    if (refreshing) {
      return refreshing;
    }

    refreshing = requestNewTokens();

    try {
      return await refreshing;
    } finally {
      refreshing = null;
    }
  };

  return {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearTokens,
    refreshAccessToken
  };
};

export type TokenStore = ReturnType<typeof createTokenStore>;

// passport / upload-session token, used by Order・Payment・Upload APIs
export const apiTokens = createTokenStore('access_token', 'refresh_token');

// WebLogin token, used by the passport bind/list/unbind flow
export const sessionTokens = createTokenStore(
  'session_access_token',
  'session_refresh_token',
  () => {
    sessionTokens.clearTokens();
    apiTokens.clearTokens();
    void webAuth.logout();
    window.location.replace('/login');
  }
);

export const { getAccessToken, setTokens, clearTokens } = apiTokens;

export const isLoggedIn = () => Boolean(sessionTokens.getAccessToken());

const SILENT_FIREBASE_ERROR_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request'
];

export const isSilentFirebaseError = (error: unknown) =>
  error instanceof Error &&
  'code' in error &&
  SILENT_FIREBASE_ERROR_CODES.includes(String(error.code));

const exchangeToken = async (credential: UserCredential) => {
  const idToken = await credential.user.getIdToken();

  try {
    const { accessToken, refreshToken } =
      await accountApi.webMixerServiceWebLogin({
        body: { idToken }
      });

    sessionTokens.setTokens(accessToken, refreshToken);

    return true;
  } catch (error) {
    await webAuth.logout();
    throw error;
  }
};

export const loginWithGoogle = async () => {
  const credential = await webAuth.googleLogin();

  if (!credential) {
    return;
  }

  return exchangeToken(credential);
};

export const sendLoginLink = async (email: string) => {
  await accountApi.webMixerServiceSendLoginEmail({ body: { email } });
  localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
};

const getLinkParam = (key: string) =>
  new URLSearchParams(window.location.search).get(key) ?? '';

export const isLoginLink = () => Boolean(getLinkParam('oobCode'));

export const clearLoginLink = () =>
  window.history.replaceState(null, '', window.location.pathname);

export const getEmailForSignIn = () =>
  getLinkParam('email') || (localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? '');

export const loginWithLink = async (email: string) => {
  const { accessToken, refreshToken } =
    await accountApi.webMixerServiceEmailLogin({
      body: { email, oobCode: getLinkParam('oobCode') }
    });

  sessionTokens.setTokens(accessToken, refreshToken);
  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);

  return true;
};

export const logout = async () => {
  sessionTokens.clearTokens();
  apiTokens.clearTokens();
  resetLocale();
  await webAuth.logout();
};
